import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { GET, POST, PUT } from '../service/base-api-service';
import { getUser } from './auth.store';

const MAX_VISIBLE_LATEST_NOTIFICATIONS = 5;

const NOTIFICATIONS_SEED = [
  {
    id: 'welcome-notification',
    title: 'Welcome To MineARC',
    message: 'You can now use the bell icon to view announcements and updates.',
    createdAt: '2026-06-09T08:00:00.000Z',
    author: 'System',
    isRead: false,
  },
];

const initialState = {
  list: NOTIFICATIONS_SEED,
  isLoading: false,
  isPublishing: false,
  isMarkingRead: false,
  error: null,
};

const normalizeThunkError = (err, fallbackMessage) => ({
  status: err?.status,
  message: err?.message || err?.data?.message || fallbackMessage,
  data: err?.data,
});

const normalizeTimestamp = (timestamp) => {
  if (!timestamp) {
    return new Date().toISOString();
  }

  // If string, assume ISO format or parse as is
  if (typeof timestamp === 'string') {
    return timestamp;
  }

  // If number, check if it's seconds (unix timestamp) or milliseconds
  if (typeof timestamp === 'number') {
    // If the number is less than 10 billion, it's likely seconds; otherwise milliseconds
    const ts = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    return new Date(ts).toISOString();
  }

  return new Date().toISOString();
};

const normalizeNotification = (item, currentUserId) => {
  const readBy = Array.isArray(item?.readBy) ? item.readBy : [];

  const isRead =
    typeof item?.isRead === 'boolean'
      ? item.isRead
      : typeof item?.read === 'boolean'
      ? item.read
      : readBy.includes(currentUserId) || Boolean(item?.readAt);

  return {
    id: String(item?.id || `notification-${Date.now()}-${Math.random()}`),
    title: item?.title || 'New Announcement',
    message: item?.message || item?.content || '',
    createdAt: normalizeTimestamp(item?.createdAt),
    author: item?.author || item?.createdBy || 'System',
    isRead,
  };
};

const normalizeList = (list, currentUserId) =>
  (Array.isArray(list) ? list : [])
    .map((item) => normalizeNotification(item, currentUserId))
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

/*
Backend contract for Copilot (notifications API)

1) GET v1/notifications
   Query params:
     - userId: string (optional)
   Response: Notification[]

2) POST v1/notifications
   Request body:
     {
       title: string,
       message: string,
       author: string,
       target: 'all'
     }
   Response: Notification (created)

3) PUT v1/notifications/read-all
   Request body:
     {
       userId: string,
       readAt: ISOString
     }
   Response:
     {
       success: boolean
     }

Notification shape accepted by FE normalizer:
{
  id: string | number,
  title: string,
  message?: string,
  content?: string,
  author?: string,
  createdBy?: string,
  createdAt: ISOString,
  isRead?: boolean,
  read?: boolean,
  readAt?: ISOString,
  readBy?: string[]
}
*/

export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getUser(getState());
      const userId = currentUser?.sub;
      const data = await GET('v1/notifications', { params: { userId } });
      return normalizeList(data, userId);
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to load notifications'));
    }
  },
);

export const publishNotification = createAsyncThunk(
  'notifications/publish',
  async ({ message, title = 'New Announcement' }, { getState, rejectWithValue }) => {
    try {
      const currentUser = getUser(getState());
      const payload = {
        title,
        message,
        author: currentUser?.user_full_name || 'Admin',
        target: 'all',
      };

      const data = await POST('v1/notifications', payload);
      return normalizeNotification(data || payload, currentUser?.sub);
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to publish notification'));
    }
  },
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getUser(getState());
      const userId = currentUser?.sub;

      await PUT('v1/notifications/read-all', {
        userId,
        readAt: new Date().toISOString(),
      });

      return { userId };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to mark notifications as read'));
    }
  },
);

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchNotifications.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [fetchNotifications.rejected]: (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to load notifications';
    },
    [fetchNotifications.fulfilled]: (state, action) => {
      state.isLoading = false;
      const nextList = Array.isArray(action.payload) ? action.payload : [];
      state.list = nextList.length ? nextList : NOTIFICATIONS_SEED;
      state.error = null;
    },

    [publishNotification.pending]: (state) => {
      state.isPublishing = true;
      state.error = null;
    },
    [publishNotification.rejected]: (state, action) => {
      state.isPublishing = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to publish notification';
    },
    [publishNotification.fulfilled]: (state, action) => {
      state.isPublishing = false;
      if (action.payload) {
        state.list = normalizeList([action.payload, ...(state.list || [])]);
      }
      state.error = null;
    },

    [markAllNotificationsRead.pending]: (state) => {
      state.isMarkingRead = true;
    },
    [markAllNotificationsRead.rejected]: (state, action) => {
      state.isMarkingRead = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to mark notifications as read';
    },
    [markAllNotificationsRead.fulfilled]: (state) => {
      state.isMarkingRead = false;
      state.list = (state.list || []).map((item) => ({ ...item, isRead: true }));
      state.error = null;
    },
  },
});

const getNotificationsStore = (state) => state.notifications;

export const getNotifications = createSelector(getNotificationsStore, (state) => state.list || []);

export const getNotificationsLoading = createSelector(getNotificationsStore, (state) => state.isLoading);

export const getNotificationsPublishing = createSelector(getNotificationsStore, (state) => state.isPublishing);

export const getNotificationsError = createSelector(getNotificationsStore, (state) => state.error);

export const getUnreadNotificationCount = createSelector(
  getNotifications,
  (list) => list.filter((item) => !item.isRead).length,
);

export const getVisibleNotifications = createSelector(getNotifications, (list) => {
  if (list.length <= MAX_VISIBLE_LATEST_NOTIFICATIONS) {
    return list;
  }

  return list.filter((item, index) => index < MAX_VISIBLE_LATEST_NOTIFICATIONS || !item.isRead);
});

export default notificationsSlice.reducer;
